<template>
  <q-dialog
    v-model="model"
    :position="$device.isMobile ? 'bottom' : 'right'"
    :maximized="$device.isMobile"
    transition-show="slide-left"
    transition-hide="slide-right"
    no-route-dismiss
    class="all-filters-drawer"
  >
    <div class="all-filters-drawer__inner" :class="{ mobile: $device.isMobile }">
      <header class="all-filters-drawer__header">
        <p class="text-subtitle text-bold">
          {{ $t("directory.filters.all_filters") }}
        </p>
        <button
          type="button"
          class="all-filters-drawer__close"
          :aria-label="$t('directory.filters.apply')"
          @click="model = false"
        >
          <base-icon name="base/Close" size="20px" />
        </button>
      </header>

      <div class="all-filters-drawer__body">
        <section
          v-if="props.propertyType !== 'lands' && props.subtypeCheckboxes.length"
          class="filter-section"
        >
          <h4 class="text-body-1 text-bold filter-section__title">
            {{ $t("directory.filters.type") }}
          </h4>
          <base-navigator-filters-checkbox
            v-for="item in props.subtypeCheckboxes"
            :key="`subtype-${item.server_label}`"
            :checkbox-label="item.label"
            :label-amount="item.count"
            :checked="item.checked"
            :is-mobile="false"
            :is-zero-amount-displayed="true"
            :placeholder-checked="false"
            @click="props.handleSubtypeCheckboxClick(item)"
          />
        </section>

        <section
          v-if="props.countryCheckboxes.length"
          class="filter-section"
        >
          <h4 class="text-body-1 text-bold filter-section__title">
            {{ $t("directory.filters.country") }}
          </h4>
          <input
            v-model="countryFilter"
            type="text"
            :placeholder="$t('search_filters.fr_by_coun')"
            class="filter-section__search"
          >
          <base-navigator-filters-checkbox
            v-for="item in visibleCountryCheckboxes"
            :key="`country-${item.server_label}`"
            :checkbox-label="countryLabel(item.label)"
            :label-amount="item.count"
            :checked="item.checked"
            :is-mobile="false"
            :is-zero-amount-displayed="true"
            :placeholder-checked="false"
            @click="props.handleCountryCheckboxClick(item)"
          />
          <button
            v-if="canToggleCountries"
            type="button"
            class="filter-section__toggle text-body-2 text-bold"
            @click="countriesExpanded = !countriesExpanded"
          >
            {{
              countriesExpanded
                ? $t("directory.filters.show_less")
                : $t("directory.filters.show_more")
            }}
          </button>
          <p
            v-if="!filteredCountryCheckboxes.length"
            class="filter-section__empty text-body-3"
          >
            {{ $t("main.m_no_results") }}
          </p>
        </section>

        <section class="filter-section">
          <h4 class="text-body-1 text-bold filter-section__title">
            {{ $t("directory.filters.price_range") }}
          </h4>
          <base-price-range-slider
            :max-price="filtersStoreRef.priseRange?.priceMax"
            :min-price="filtersStoreRef.priseRange?.priceMin"
            :range="filtersStoreRef.priseRange?.range"
            style-type="filters"
            no-delay
            :drop-func="dropPrice"
            @emit-range="
              (data) => props.updateSliderFilter('price_range', 'tokenPrice', data)
            "
          />
        </section>

        <section v-if="props.propertyType !== 'lands'" class="filter-section">
          <h4 class="text-body-1 text-bold filter-section__title">
            {{ $t("directory.filters.yield_apr") }}
          </h4>
          <base-navigator-filters-input-range
            name="apr"
            after="%"
            :maxlength="3"
            is-slider
            :slider-max="filtersStoreRef.currentStats?.aprMax"
            :slider-min="filtersStoreRef.currentStats?.aprMin"
            :drop-func="dropApr"
            @change="(data) => props.updateSliderFilter('apr', 'apr', data)"
          />
        </section>

        <section v-if="props.propertyType !== 'lands'" class="filter-section">
          <h4 class="text-body-1 text-bold filter-section__title">
            {{ $t("directory.filters.yield_irr") }}
          </h4>
          <base-navigator-filters-input-range
            name="irr"
            after="%"
            :maxlength="3"
            is-slider
            :slider-max="filtersStoreRef.currentStats?.irrMax"
            :slider-min="filtersStoreRef.currentStats?.irrMin"
            :drop-func="dropIrr"
            @change="(data) => props.updateSliderFilter('irr', 'irr', data)"
          />
        </section>

        <section class="filter-section">
          <h4 class="text-body-1 text-bold filter-section__title">
            {{ $t("directory.filters.min_investment") }}
          </h4>
          <base-navigator-filters-input-range
            name="minimumInvestment"
            is-slider
            :slider-max="filtersStoreRef.currentStats?.maximumInvestment"
            :slider-min="filtersStoreRef.currentStats?.minimumInvestment"
            :drop-func="dropMinInvest"
            conver-able
            @change="
              (data) =>
                props.updateSliderFilter('min_invest', 'minimumInvestment', data)
            "
          />
        </section>

        <section
          v-if="props.documentsCheckboxes.length"
          class="filter-section"
        >
          <h4 class="text-body-1 text-bold filter-section__title">
            {{ $t("directory.filters.documents") }}
          </h4>
          <base-navigator-filters-checkbox
            v-for="item in props.documentsCheckboxes"
            :key="`doc-${item.server_label}`"
            :checkbox-label="item.label"
            :label-amount="item.count"
            :checked="item.checked"
            :is-mobile="false"
            :is-zero-amount-displayed="true"
            :placeholder-checked="false"
            @click="props.handleDocumentsCheckboxClick(item)"
          />
        </section>

        <section
          v-if="props.propertyType !== 'lands' && props.bedroomCheckboxes.length"
          class="filter-section"
        >
          <h4 class="text-body-1 text-bold filter-section__title">
            {{ $t("directory.filters.bedrooms") }}
          </h4>
          <base-navigator-filters-checkbox
            v-for="item in props.bedroomCheckboxes"
            :key="`bed-${item.server_label}`"
            :checkbox-label="item.label"
            :label-amount="item.count"
            :checked="item.checked"
            :is-mobile="false"
            :is-zero-amount-displayed="true"
            :placeholder-checked="false"
            @click="props.handleBedroomCheckboxClick(item)"
          />
        </section>

        <section
          v-if="props.propertyType !== 'lands' && props.bathroomCheckboxes.length"
          class="filter-section"
        >
          <h4 class="text-body-1 text-bold filter-section__title">
            {{ $t("directory.filters.bathrooms") }}
          </h4>
          <base-navigator-filters-checkbox
            v-for="item in props.bathroomCheckboxes"
            :key="`bath-${item.server_label}`"
            :checkbox-label="item.label"
            :label-amount="item.count"
            :checked="item.checked"
            :is-mobile="false"
            :is-zero-amount-displayed="true"
            :placeholder-checked="false"
            @click="props.handleBathroomCheckboxClick(item)"
          />
        </section>

        <section
          v-if="props.companyCheckboxes.length"
          class="filter-section"
        >
          <h4 class="text-body-1 text-bold filter-section__title">
            {{ $t("directory.filters.providers") }}
          </h4>
          <base-navigator-filters-checkbox
            v-for="item in props.companyCheckboxes"
            :key="`provider-${item.server_label}`"
            :checkbox-label="item.label"
            :label-amount="item.count"
            :checked="item.checked"
            :is-mobile="false"
            :is-zero-amount-displayed="true"
            :placeholder-checked="false"
            @click="props.handleCompanyCheckboxClick(item)"
          />
        </section>

        <section
          v-if="props.propertyType !== 'lands' && props.stageCheckboxes.length"
          class="filter-section"
        >
          <h4 class="text-body-1 text-bold filter-section__title">
            {{ $t("directory.filters.stage") }}
          </h4>
          <base-navigator-filters-checkbox
            v-for="item in props.stageCheckboxes"
            :key="`stage-${item.server_label}`"
            :checkbox-label="
              $t(`search_filters.fr_stages.${item.label.replace(/ /g, '_')}`)
            "
            :label-amount="item.count"
            :checked="item.checked"
            :is-mobile="false"
            :is-zero-amount-displayed="true"
            :placeholder-checked="false"
            @click="props.handleStageCheckboxClick(item)"
          />
        </section>

        <section
          v-if="props.showRating && props.ratingCheckboxes.length"
          class="filter-section"
        >
          <h4 class="text-body-1 text-bold filter-section__title">
            {{ $t("directory.filters.rating") }}
          </h4>
          <base-navigator-filters-checkbox
            v-for="item in props.ratingCheckboxes"
            :key="`rating-${item.server_label}`"
            :checkbox-label="item.label"
            :label-amount="item.count"
            :checked="item.checked"
            :is-mobile="false"
            :is-zero-amount-displayed="true"
            :placeholder-checked="false"
            @click="props.handleRatingCheckboxClick(item)"
          >
            <template v-if="item.label !== $t('search_filters.fr_no_ratings')">
              <div
                v-for="i in Array.from({
                  length: isNaN(parseInt(item.label))
                    ? 0
                    : parseInt(item.label),
                })"
                :key="`star-${item.server_label}-${i}`"
                class="rating-star"
              >
                <base-icon
                  name="base/StarFull"
                  color="#F59914"
                  size="14px"
                  filled
                />
              </div>
            </template>
            <p v-else>{{ item.label }}</p>
          </base-navigator-filters-checkbox>
        </section>
      </div>

      <footer class="all-filters-drawer__footer">
        <button
          type="button"
          class="all-filters-drawer__show text-body-1 text-bold"
          @click="model = false"
        >
          {{
            $t("directory.filters.show_results", { count: props.resultsCount })
          }}
        </button>
        <button
          type="button"
          class="all-filters-drawer__clear text-body-2 text-bold"
          @click="$emit('clear-all')"
        >
          {{ $t("directory.filters.clear_all") }}
        </button>
      </footer>
    </div>
  </q-dialog>
</template>

<script setup lang="ts">
import { filtersStore } from "~/store/search_filters";
import { countryNames } from "~/utilities/constants/countryCodes";
import eventBus from "~/utilities/composables/eventBus";
import type {
  FilterCheckbox,
  PropertyTypes,
} from "~/components/base/navigator/types";

interface Props {
  modelValue: boolean;
  propertyType: PropertyTypes;
  resultsCount: number;
  showRating?: boolean;
  bedroomCheckboxes: FilterCheckbox[];
  bathroomCheckboxes: FilterCheckbox[];
  stageCheckboxes: FilterCheckbox[];
  companyCheckboxes: FilterCheckbox[];
  documentsCheckboxes: FilterCheckbox[];
  ratingCheckboxes: FilterCheckbox[];
  countryCheckboxes: FilterCheckbox[];
  subtypeCheckboxes: FilterCheckbox[];
  handleBedroomCheckboxClick: (item: FilterCheckbox) => void;
  handleBathroomCheckboxClick: (item: FilterCheckbox) => void;
  handleStageCheckboxClick: (item: FilterCheckbox) => void;
  handleCompanyCheckboxClick: (item: FilterCheckbox) => void;
  handleDocumentsCheckboxClick: (item: FilterCheckbox) => void;
  handleRatingCheckboxClick: (item: FilterCheckbox) => void;
  handleCountryCheckboxClick: (item: FilterCheckbox) => void;
  handleSubtypeCheckboxClick: (item: FilterCheckbox) => void;
  updateSliderFilter: (
    key: string,
    paramName: string,
    values: Record<string, Record<string, number | string>>
  ) => void;
}

const props = defineProps<Props>();
const emit = defineEmits<{
  "update:modelValue": [val: boolean];
  "clear-all": [];
}>();

const filtersStoreRef = filtersStore();
const { t } = useI18n();

const countryFilter = ref("");
const countriesExpanded = ref(false);
const COUNTRY_COLLAPSED_LIMIT = 7;

const model = computed({
  get: () => props.modelValue,
  set: (val: boolean) => emit("update:modelValue", val),
});

const countryLabel = (label: string) => {
  if (!label || label === "null") return t("search_filters.fr_others");
  const localized =
    t(`countries.${label}`) !== `countries.${label}`
      ? t(`countries.${label}`)
      : countryNames[label];
  return localized || label;
};

const sortedCountryCheckboxes = computed(() =>
  [...props.countryCheckboxes].sort((a, b) => (b.count || 0) - (a.count || 0))
);

const isSearchingCountries = computed(
  () => countryFilter.value.trim().length > 0
);

const filteredCountryCheckboxes = computed(() => {
  const q = countryFilter.value.trim().toLowerCase();
  if (!q) return sortedCountryCheckboxes.value;
  return sortedCountryCheckboxes.value.filter((item) =>
    countryLabel(item.label).toLowerCase().includes(q)
  );
});

const visibleCountryCheckboxes = computed(() => {
  if (isSearchingCountries.value || countriesExpanded.value) {
    return filteredCountryCheckboxes.value;
  }
  return filteredCountryCheckboxes.value.slice(0, COUNTRY_COLLAPSED_LIMIT);
});

const canToggleCountries = computed(
  () =>
    !isSearchingCountries.value &&
    filteredCountryCheckboxes.value.length > COUNTRY_COLLAPSED_LIMIT
);

const dropPrice = () => {
  return function (ctx: (nums: [number, number]) => void) {
    eventBus.on("drop-tokenPrice-range", ctx);
  };
};
const dropApr = () => {
  return function (ctx: (nums: [number, number]) => void) {
    eventBus.on("drop-apr-range", ctx);
  };
};
const dropIrr = () => {
  return function (ctx: (nums: [number, number]) => void) {
    eventBus.on("drop-irr-range", ctx);
  };
};
const dropMinInvest = () => {
  return function (ctx: (nums: [number, number]) => void) {
    eventBus.on("drop-minimumInvestment-range", ctx);
  };
};
</script>

<style lang="scss">
.all-filters-drawer.q-dialog .q-dialog__inner {
  padding: 0;
}
.all-filters-drawer.q-dialog .q-dialog__inner > div {
  max-height: 100dvh;
  height: 100dvh;
  border-radius: 0;
  overflow-x: hidden;
}
.all-filters-drawer.q-dialog .q-dialog__inner--right {
  padding: 0;
}
.all-filters-drawer.q-dialog .q-dialog__inner--bottom > div {
  width: 100vw;
  max-width: 100vw;
}

// Inside the drawer the price-range slider must fill the column. The
// .base-price-range--filters variant defaults to max-width: 189px (designed
// for the desktop top-bar popover), which leaves dead horizontal space and
// can let the inner chart bars push the body sideways. Force full width here.
.all-filters-drawer .base-price-range.base-price-range--filters {
  width: 100%;
  min-width: 0;
  max-width: 100%;
}
</style>

<style scoped lang="scss">
.all-filters-drawer__inner {
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 560px;
  height: 100vh;
  background: var(--white-monochrome);
  overflow-x: hidden;
  box-sizing: border-box;
  &.mobile {
    width: 100vw;
    max-width: 100vw;
    height: 100dvh;
    border-radius: 16px 16px 0 0;
  }
}

.all-filters-drawer__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  border-bottom: 1px solid var(--border-subtle);
  p {
    margin: 0;
  }
}

.all-filters-drawer__close {
  @include drop-button-styles;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  cursor: pointer;
  &:hover {
    background: var(--surface-muted);
  }
}

.all-filters-drawer__body {
  flex: 1 1 auto;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 8px 24px 24px;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  width: 100%;
  min-width: 0;
}

.filter-section {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 20px 0;
  border-bottom: 1px solid var(--border-subtle);
  &:last-child {
    border-bottom: none;
  }
  &__title {
    margin: 0 0 4px;
    color: var(--text-primary);
  }
  &__search {
    width: 100%;
    padding: 10px 12px;
    border: 1px solid var(--border-subtle);
    border-radius: 10px;
    font-size: 14px;
    background: var(--white-monochrome);
    outline: none;
    color: var(--text-primary);
    &:focus {
      border-color: var(--border-strong);
    }
    &::placeholder {
      color: var(--text-secondary);
    }
  }
  &__empty {
    padding: 8px 4px;
    color: var(--text-secondary);
  }
  &__toggle {
    @include drop-button-styles;
    align-self: flex-start;
    padding: 4px 0;
    color: var(--text-primary);
    text-decoration: underline;
    text-underline-offset: 3px;
    cursor: pointer;
    &:hover {
      color: var(--text-secondary);
    }
  }
  :deep(.checkbox-wrapper) p {
    max-width: none;
    white-space: normal;
    overflow: visible;
    text-overflow: clip;
  }
}

.rating-star {
  display: inline-flex;
}

.all-filters-drawer__footer {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 12px;
  padding: 16px 24px 24px;
  border-top: 1px solid var(--border-subtle);
  background: var(--surface);
}

.all-filters-drawer__clear {
  @include drop-button-styles;
  @include ds-tertiary;
  align-self: center;
  color: var(--text-secondary);
  &:hover {
    color: var(--text-primary);
  }
}

.all-filters-drawer__show {
  @include drop-button-styles;
  @include ds-secondary;
  width: 100%;
  height: 44px;
  padding: 0 24px;
  border-radius: var(--radius-pill);
  font-size: var(--font-small);
  font-weight: var(--fw-bold);
}

@media (max-width: 30rem) {
  .all-filters-drawer__header {
    padding: 16px 16px;
  }
  .all-filters-drawer__body {
    padding: 8px 16px 16px;
  }
  .all-filters-drawer__footer {
    padding: 12px 16px 16px;
  }
}
</style>
